// PMI grade -> chemical composition (Min/Max %) data.
//
// This file is the single source of truth for grade -> element composition.
// The chemical-element columns shown in the PMI screen are generated from
// the selected grade here; the fixed material columns are handled separately.
//
// Each element uses { symbol, min, max }.
// - Use '' when a limit is not specified.
// - A value of 'Balance' is used for specifications that explicitly show
//   "Balance" (currently Ni for Hastelloy C22). The UI/PDF treats it as a
//   special one-line limit instead of Min/Max.

const pmiGrades = [
  {
    name: 'HE30',
    elements: [
      { symbol: 'Mn', min: '0.40', max: '1.00' },
      { symbol: 'Cu', min: '', max: '0.10' },
      { symbol: 'Zn', min: '0.00', max: '0.20' },
      { symbol: 'Fe', min: '', max: '0.50' },
    ],
  },
  {
    // Existing grade retained; its latest supplied PMI report only shows
    // Cr and Mn with these limits.
    name: 'ALU6061',
    aliases: ['Aluminum 6061', 'Aluminium 6061'],
    elements: [
      { symbol: 'Cr', min: '0.04', max: '0.35' },
      { symbol: 'Mn', min: '0.80', max: '1.20' },
    ],
  },
  {
    // Existing D2 grade updated from the supplied PMI report: V was added.
    name: 'D2',
    elements: [
      { symbol: 'Cr', min: '11.00', max: '13.00' },
      { symbol: 'Mo', min: '0.70', max: '1.20' },
      { symbol: 'Mn', min: '0.00', max: '0.60' },
      { symbol: 'Ni', min: '0.00', max: '0.30' },
      { symbol: 'V', min: '0.80', max: '1.20' },
    ],
  },
  {
    name: 'SS316',
    elements: [
      { symbol: 'Cr', min: '16.00', max: '18.00' },
      { symbol: 'Mn', min: '', max: '2.00' },
      { symbol: 'Ni', min: '10.00', max: '14.00' },
      { symbol: 'Mo', min: '2.00', max: '3.00' },
    ],
  },
  {
    name: 'SS304',
    elements: [
      { symbol: 'Cr', min: '18.00', max: '20.00' },
      { symbol: 'Mn', min: '', max: '2.00' },
      { symbol: 'Ni', min: '8.00', max: '10.00' },
    ],
  },
  {
    name: 'EN41B',
    elements: [
      { symbol: 'Cr', min: '1.40', max: '1.80' },
      { symbol: 'Mn', min: '', max: '0.65' },
      { symbol: 'Mo', min: '0.10', max: '0.25' },
      { symbol: 'Ni', min: '', max: '0.40' },
    ],
  },
  {
    name: 'EN24',
    elements: [
      { symbol: 'Cr', min: '1.00', max: '1.40' },
      { symbol: 'Ni', min: '1.30', max: '1.70' },
      { symbol: 'Mn', min: '0.45', max: '0.70' },
      { symbol: 'Mo', min: '0.20', max: '0.35' },
    ],
  },
  {
    name: 'EN19',
    elements: [
      { symbol: 'Mn', min: '0.50', max: '0.80' },
      { symbol: 'Cr', min: '0.90', max: '1.50' },
      { symbol: 'Mo', min: '0.20', max: '0.40' },
    ],
  },
  {
    name: 'SS410/420',
    elements: [
      { symbol: 'Cr', min: '11.50', max: '13.50' },
      { symbol: 'Si', min: '', max: '1.00' },
      { symbol: 'Mn', min: '', max: '1.00' },
    ],
  },
  {
    name: 'ALU7075',
    elements: [
      { symbol: 'Mn', min: '', max: '0.3' },
      { symbol: 'Zn', min: '5.1', max: '6.1' },
      { symbol: 'Cu', min: '1.2', max: '2.00' },
      { symbol: 'Fe', min: '', max: '0.5' },
      { symbol: 'Cr', min: '0.18', max: '0.28' },
    ],
  },
  {
    name: 'ALU 5058',
    aliases: ['ALU5058', 'Alu 5058'],
    elements: [
      { symbol: 'Al', min: '95.02', max: '98.30' },
      { symbol: 'Mn', min: '0.40', max: '1.00' },
      { symbol: 'Cr', min: '', max: '0.25' },
      { symbol: 'Zn', min: '0.00', max: '0.20' },
      { symbol: 'Fe', min: '', max: '0.50' },
    ],
  },
  {
    name: 'EN8',
    elements: [
      { symbol: 'Mn', min: '0.10', max: '0.40' },
    ],
  },
  {
    name: '20MNCR5',
    elements: [
      { symbol: 'Mn', min: '1.00', max: '1.30' },
      { symbol: 'Cr', min: '0.80', max: '1.10' },
    ],
  },
  {
    name: 'EN36',
    elements: [
      { symbol: 'Si', min: '0.10', max: '0.35' },
      { symbol: 'Mn', min: '0.60', max: '0.80' },
    ],
  },
  {
    name: 'EN8D/C45',
    elements: [
      { symbol: 'Mn', min: '0.70', max: '0.90' },
    ],
  },
  {
    name: 'SS431',
    elements: [
      { symbol: 'Cr', min: '18.00', max: '20.00' },
      { symbol: 'Mn', min: '', max: '2.00' },
      { symbol: 'Ni', min: '8.00', max: '10.00' },
    ],
  },

  // Grades added / updated from the 35 supplied PMI report photos.
  {
    name: 'EN31',
    elements: [
      { symbol: 'Cr', min: '1.00', max: '1.60' },
      { symbol: 'Mn', min: '0.30', max: '0.70' },
    ],
  },
  {
    name: 'AISI O1',
    elements: [
      { symbol: 'Cr', min: '0.40', max: '0.60' },
      { symbol: 'Mn', min: '1.00', max: '1.40' },
      { symbol: 'W', min: '0.40', max: '0.60' },
    ],
  },
  {
    name: 'AISI 4340',
    elements: [
      { symbol: 'Mn', min: '0.60', max: '0.80' },
      { symbol: 'Cr', min: '0.70', max: '0.90' },
      { symbol: 'Ni', min: '1.65', max: '2.00' },
    ],
  },
  {
    name: 'EN42',
    elements: [
      { symbol: 'Cr', min: '0.70', max: '0.85' },
      { symbol: 'Mn', min: '0.60', max: '0.70' },
      { symbol: 'Si', min: '0.05', max: '0.40' },
    ],
  },
  {
    name: 'H13',
    elements: [
      { symbol: 'Cr', min: '4.75', max: '5.50' },
      { symbol: 'Mo', min: '1.10', max: '1.75' },
      { symbol: 'V', min: '0.80', max: '1.20' },
      { symbol: 'Mn', min: '0.20', max: '0.60' },
    ],
  },
  {
    name: 'MONEL K500',
    aliases: ['Monel K500'],
    elements: [
      { symbol: 'Cu', min: '27.00', max: '33.00' },
      { symbol: 'Ni', min: '63.00', max: '' },
      { symbol: 'Mn', min: '', max: '1.50' },
      { symbol: 'Fe', min: '', max: '2.00' },
    ],
  },
  {
    name: 'C45',
    aliases: ['C-45'],
    elements: [
      { symbol: 'Mn', min: '0.50', max: '0.90' },
    ],
  },
  {
    name: 'SAE 1018',
    aliases: ['SAE1018'],
    elements: [
      { symbol: 'Cr', min: '', max: '0.15' },
      { symbol: 'Mn', min: '0.60', max: '0.90' },
      { symbol: 'Ni', min: '', max: '0.20' },
    ],
  },
  {
    name: 'SAE 8620',
    aliases: ['S8620', 'SAE8620'],
    elements: [
      { symbol: 'Cr', min: '0.45', max: '0.60' },
      { symbol: 'Mn', min: '0.70', max: '0.90' },
      { symbol: 'Ni', min: '0.45', max: '0.70' },
      { symbol: 'Mo', min: '0.15', max: '0.25' },
    ],
  },
  {
    name: 'K 310',
    aliases: ['K310'],
    elements: [
      { symbol: 'Cr', min: '4.80', max: '5.50' },
      { symbol: 'V', min: '0.40', max: '0.60' },
      { symbol: 'Mo', min: '1.10', max: '1.50' },
    ],
  },
  {
    name: 'SS301',
    aliases: ['SS 301'],
    elements: [
      { symbol: 'Cr', min: '16.00', max: '18.00' },
      { symbol: 'Ni', min: '6.00', max: '8.00' },
      { symbol: 'Mn', min: '', max: '2.00' },
    ],
  },
  {
    name: 'XM19',
    aliases: ['XM 19'],
    elements: [
      { symbol: 'Cr', min: '20.50', max: '23.50' },
      { symbol: 'Ni', min: '11.50', max: '13.50' },
      { symbol: 'Mn', min: '4.00', max: '6.00' },
      { symbol: 'Mo', min: '1.50', max: '3.00' },
    ],
  },
  {
    name: 'SS316L',
    aliases: ['SS 316L'],
    elements: [
      { symbol: 'Cr', min: '16.00', max: '18.00' },
      { symbol: 'Ni', min: '10.00', max: '14.00' },
      { symbol: 'Mn', min: '', max: '2.00' },
      { symbol: 'Mo', min: '2.00', max: '3.00' },
    ],
  },
  {
    name: 'SS321',
    aliases: ['SS 321'],
    elements: [
      { symbol: 'Cr', min: '17.00', max: '19.00' },
      { symbol: 'Ni', min: '9.00', max: '12.00' },
      { symbol: 'Mn', min: '', max: '2.00' },
      { symbol: 'Ti', min: '', max: '0.70' },
    ],
  },
  {
    name: 'AISI 4140',
    elements: [
      { symbol: 'Cr', min: '0.80', max: '1.10' },
      { symbol: 'Mn', min: '0.75', max: '1.00' },
      { symbol: 'Ni', min: '', max: '0.25' },
    ],
  },
  {
    name: 'Titanium Grade 2',
    aliases: ['Titanium Gr.2', 'Titanium Gr 2'],
    elements: [
      { symbol: 'Ti', min: '98.90', max: '' },
    ],
  },
  {
    name: 'SS304L',
    aliases: ['SS 304L'],
    elements: [
      { symbol: 'Cr', min: '18.00', max: '20.00' },
      { symbol: 'Ni', min: '8.00', max: '10.50' },
      { symbol: 'Mn', min: '', max: '2.00' },
    ],
  },
  {
    name: 'ZqSn10-2 (Tin Bronze)',
    aliases: ['ZQSn10-2 (Tin Bronze)', 'ZqSn10-2'],
    elements: [
      { symbol: 'Zn', min: '1.75', max: '2.75' },
      { symbol: 'Pb', min: '0.00', max: '1.50' },
      { symbol: 'Sn', min: '9.00', max: '10.50' },
      { symbol: 'Fe', min: '0.00', max: '0.15' },
      { symbol: 'Ni', min: '0.00', max: '1.00' },
    ],
  },
  {
    name: 'SS430',
    aliases: ['SS 430'],
    elements: [
      { symbol: 'Cr', min: '16.00', max: '18.00' },
      { symbol: 'Ni', min: '', max: '0.75' },
      { symbol: 'Mn', min: '', max: '1.00' },
    ],
  },
  {
    name: 'Titanium Gr.5',
    aliases: ['Titanium Gr 5', 'Titanium Grade 5'],
    elements: [
      { symbol: 'Ti', min: '', max: '' },
    ],
  },
  {
    name: 'Inconel X-750',
    aliases: ['INCONEL X-750', 'Inconel X750'],
    elements: [
      { symbol: 'Cr', min: '14.00', max: '17.00' },
      { symbol: 'Ni', min: '70.00', max: '' },
      { symbol: 'Ti', min: '2.25', max: '2.75' },
      { symbol: 'Mn', min: '', max: '1.00' },
      { symbol: 'Fe', min: '5.00', max: '9.00' },
    ],
  },
  {
    name: 'EN1A',
    elements: [
      { symbol: 'Mn', min: '0.80', max: '1.20' },
    ],
  },
  {
    name: 'Monel K400',
    elements: [
      { symbol: 'Cu', min: '28.00', max: '34.00' },
      { symbol: 'Ni', min: '63.00', max: '70.00' },
      { symbol: 'Mn', min: '', max: '2.00' },
      { symbol: 'Fe', min: '', max: '2.50' },
    ],
  },
  {
    name: 'Aluminium 6063',
    aliases: ['ALU6063', 'Aluminum 6063'],
    elements: [
      { symbol: 'Al', min: '97.50', max: '99.35' },
      { symbol: 'Fe', min: '', max: '0.35' },
      { symbol: 'Zn', min: '', max: '0.10' },
    ],
  },
  {
    name: '17.4 PH',
    elements: [
      { symbol: 'Cr', min: '15.00', max: '17.50' },
      { symbol: 'Ni', min: '3.00', max: '5.00' },
      { symbol: 'Mn', min: '', max: '1.00' },
    ],
  },
  {
    name: 'MS',
    elements: [
      { symbol: 'Mn', min: '0.60', max: '0.90' },
    ],
  },
  {
    name: 'P20',
    elements: [
      { symbol: 'Cr', min: '1.40', max: '1.60' },
      { symbol: 'Mn', min: '0.60', max: '1.00' },
      { symbol: 'Mo', min: '0.15', max: '0.35' },
    ],
  },
  {
    name: 'Hastelloy C22',
    aliases: ['HASTELLOY C22', 'Hastelloy C-22'],
    elements: [
      { symbol: 'Cr', min: '20.00', max: '22.50' },
      { symbol: 'Ni', min: 'Balance', max: '' },
      { symbol: 'Mn', min: '', max: '0.50' },
      { symbol: 'Mo', min: '12.50', max: '14.50' },
    ],
  },
  {
    name: 'Inconel 625',
    aliases: ['INCONEL 625'],
    elements: [
      { symbol: 'Cr', min: '20.00', max: '23.00' },
      { symbol: 'Ni', min: '58.00', max: '' },
      { symbol: 'Mo', min: '8.00', max: '10.00' },
      { symbol: 'Fe', min: '', max: '5.00' },
    ],
  },
];

// Returns the option list for the Autocomplete (array of grade names).
export const getPmiGradeOptions = () => pmiGrades.map((g) => g.name);

const normalizeGradeName = (value) => String(value ?? '').trim().toLowerCase();

// Returns the element composition [{symbol,min,max}] for a given grade name.
// Aliases are supported so older saved certificates continue to resolve to
// the same current definition when a grade name is normalized.
export const getElementsForGrade = (gradeName) => {
  if (!gradeName) return [];
  const target = normalizeGradeName(gradeName);
  const found = pmiGrades.find((g) => {
    if (normalizeGradeName(g.name) === target) return true;
    return (g.aliases || []).some((alias) => normalizeGradeName(alias) === target);
  });
  return found ? found.elements : [];
};

export default pmiGrades;
